'use client'

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { BIRD_NAME } from '../../lib/world/identity'
import { BookIcon, CloseButton, kawaiiPanel } from './KawaiiButton'
import { ui, kawaii } from '../../lib/ui/theme'
import { labelFor } from '../../lib/ui/journalLabels'
import { BAG_MOVES } from '../../lib/things/bag'
import FriendFace from './FriendFace'
import { buildStories } from '../../lib/ui/stories'
import useGameStore from '../../stores/gameStore'
import { useAccount, authFetch } from '../auth/useUser'

// Open, the panel is being read and new lines should land while you
// look. Shut, the poll only feeds the unread dot, and forty seconds is
// plenty for a dot: at eight it was parsing sixty entries and rebuilding
// every story five hundred times an hour into a panel nobody had open.
const POLL_OPEN_MS = 8000
const POLL_CLOSED_MS = 40000
// how much of him is read: his day once, then each poll's worth of new lines laid over it (fetchJournal)
const DAY_LINES = 300
const POLL_LINES = 60

// ── Entry type config ─────────────────────────────────────────────────────────

// The place, as a reader would name it. Anything missing falls through to
// the raw activity key, which is how "forage" ended up sitting above a line
// about a Happy Meal.

const sentenceCase = (s) => String(s || '').charAt(0).toUpperCase() + String(s || '').slice(1)

const OUTCOME_STYLES = {
  won:         { label: 'Won',           color: '#276b3c' },
  lost:        { label: 'Lost',          color: '#a83832' },
  broke_even:  { label: 'Broke even',    color: '#8a5a12' },
  watched:     { label: 'Watched',       color: '#5f42ab' },
  browsed:     { label: 'Browsed',       color: '#175f83' },
  danced:      { label: 'Danced',        color: '#9c3568' },
  silent_rave: { label: 'Empty rave',    color: '#57506e' },
  // Internet-specific outcomes
  started:     { label: 'Opened',        color: '#5f42ab' },
  read:        { label: 'Read',          color: '#175f83' },
  looked_again: { label: 'Looked again',  color: '#175f83' },
  still_waiting: { label: 'Still waiting', color: '#8a5a12' },
  flat:        { label: 'Flat',          color: '#57506e' },
  thin:        { label: 'Thin page',     color: '#57506e' },
  unreadable:  { label: 'Unreadable',    color: '#57506e' },
  failed:      { label: 'Failed',        color: '#57506e' },
  // Forage / Happy Meal
  happy_meal:  { label: 'Happy meal',    color: '#8a5a12' },
  // The narrator's other beats. Anything not named here falls through to
  // the prettified key below, never the raw snake_case.
  visited:     { label: 'Visited',       color: '#5f42ab' },
  paddled:     { label: 'Paddled',       color: '#175f83' },
  preened:     { label: 'Preened',       color: '#9c3568' },
  drank:       { label: 'A drink',       color: '#8a5a12' },
  surveyed:    { label: 'Looked out',    color: '#175f83' },
  found:       { label: 'Found something', color: '#276b3c' },
  // The three mornings. Short is its own thing rather than a synonym for
  // rough: a short night was CHOSEN the evening before, and the pill
  // saying so is half the story.
  rested:      { label: 'Slept well',     color: '#276b3c' },
  short:       { label: 'Short night',    color: '#8a5a12' },
  rough:       { label: 'Rough night',    color: '#a83832' },
  // A want the world refused. The pill says the state of play; his line
  // says the appetite. "Not yet" read as a riddle next to the wrong
  // kind (an internet pill saying not yet over a laundrette line);
  // "Turned away" says what actually happened at the door.
  denied:      { label: 'Turned away',    color: '#8a5a12' },
  // A visitor gone for good. Grey, because it is not a wound, it is an
  // ending, and the diary treats endings as facts.
  gone:        { label: 'Gone for good',  color: '#6b6484' },
  // The piano arc's public beats: the first key, and the rare day a tier
  // gives way.
  first_touch: { label: 'First touch',    color: '#3d72b8' },
  breakthrough:{ label: 'It clicked',     color: '#a37d10' },
  // The rack beside the piano (world/Tunes.js): a jam kept, one binned,
  // the first, a poke before he could play, a sitting that came to nothing.
  self_portrait: { label: 'Drew himself',  color: '#b8743d' },
  made:        { label: 'Made a coin',     color: '#b8743d' },
  rehearsed:   { label: 'A coin, rehearsed', color: '#57506e' },
  passed:      { label: 'Decided against it', color: '#57506e' },
  traded:      { label: 'Somebody bought it', color: '#b8743d' },
  down:        { label: 'The coin is down', color: '#57506e' },
  took_up:     { label: 'Took up the synth', color: '#3d72b8' },
  jammed:      { label: 'A jam',           color: '#3d72b8' },
  binned:      { label: 'Binned a jam',    color: '#57506e' },
  poked:       { label: 'Poked the synth', color: '#57506e' },
  noodled:     { label: 'Nothing kept',    color: '#57506e' },
  vision:      { label: 'A vision',        color: '#7c5cff' },
  dreamt:      { label: 'A dream',         color: '#7c5cff' },
  settled:     { label: 'Put it down',     color: '#57506e' },
  tweeted:     { label: 'Posted it',       color: '#175f83' },
  checked:     { label: 'Had a look',      color: '#57506e' },
  nothing:     { label: 'Nothing today', color: '#57506e' },
  odd:         { label: 'Something odd', color: '#5f42ab' },
  letter:      { label: 'A letter',      color: '#276b3c' },
  morning:     { label: 'The morning',   color: '#175f83' },
  sacrifice_judged: { label: 'An offering', color: '#5f42ab' },
  shrine_sitting: { label: 'At the shrine', color: '#5f42ab' },
  answered_talk: { label: 'Answered back', color: '#8a5a12' },
  // Creative coding (world/Code.js): a program he wrote and put up, and
  // the promise he could not keep after three goes.
  wrote_program:  { label: 'Wrote a program', color: '#3d72b8' },
  // Getting better on purpose (world/Craft.js): something read to try.
  studied:        { label: 'Studied',         color: '#175f83' },
  // The pieces he writes at the piano (world/Pieces.js).
  composed:       { label: 'A piece',         color: '#b8743d' },
  scrapped:       { label: 'Scrapped a piece', color: '#57506e' },
  program_failed: { label: 'Could not make it', color: '#57506e' },
  // The friend he made on the cafe computer (world/Friend.js). The talk
  // and the rewrite carry its name in the pill (see ActivityEntry).
  made_friend:      { label: 'Made a friend',   color: '#9c3568' },
  talked_to_friend: { label: 'Talked to it',    color: '#9c3568' },
  tended_friend:    { label: 'Rewrote it',      color: '#57506e' },
  friend_started:   { label: 'It came up',      color: '#9c3568' },
  friend_left_note: { label: 'A note for him',  color: '#9c3568' },
  told_them:     { label: 'Told them what he found', color: '#8a5a12' },
  // The greenhouse. Beds that grow in watered hours and die in
  // forgotten ones; lost is red because it is his to have prevented.
  sown:        { label: 'Sowed',         color: '#276b3c' },
  watered:     { label: 'Watered',       color: '#175f83' },
  weeded:      { label: 'Weeded',        color: '#276b3c' },
  picked:      { label: 'Picked',        color: '#8a5a12' },
  ate_own:     { label: 'Ate his own',   color: '#8a5a12' },
  lost_bed:    { label: 'Lost a bed',    color: '#a83832' },
  looked_in:   { label: 'Looked in',     color: '#57506e' },
  found_map:   { label: 'Found the map', color: '#8a5a12' },
  // His hoard (world/Hoard.js): a small thing found, carried, kept.
  found_thing:   { label: 'Found a thing',   color: '#8a5a12' },
  stashed_thing: { label: 'Kept it',         color: '#57506e' },
  // His bag (sim-server world/things/bag.js). The words are lib/things/bag.js's,
  // the row the town's things kit holds every move to.
  put_away:      { label: sentenceCase(BAG_MOVES.put_away.diary), color: '#57506e' },
  take_out:      { label: sentenceCase(BAG_MOVES.take_out.diary), color: '#57506e' },
  pick_up:       { label: sentenceCase(BAG_MOVES.pick_up.diary),  color: '#8a5a12' },
  // His shop and his purse (world/PrintShop.js, world/Purse.js).
  opened_shop:    { label: 'Opened his shop',  color: '#5f42ab' },
  listed_prints:  { label: 'Prints put up',    color: '#5f42ab' },
  sold_print:     { label: 'A print sold',     color: '#276b3c' },
  posted_print:   { label: 'In the post',      color: '#175f83' },
  bought_thing:   { label: 'Bought it himself', color: '#8a5a12' },
  saving_for:     { label: 'Saving up',        color: '#57506e' },
  dry_spell:      { label: 'Nothing sold lately', color: '#57506e' },
  // His vending machine (world/VendingMachine.js), and what came out of it.
  vend:           { label: 'Vending machine',   color: '#175f83' },
  stuck:          { label: 'Stuck on the spiral', color: '#8a5a12' },
  shake:          { label: 'Shook the machine', color: '#57506e' },
  tray:           { label: 'Out of the tray',   color: '#276b3c' },
  code:           { label: 'Tried a code',      color: '#57506e' },
  keypad_open:    { label: 'The keypad opened', color: '#276b3c' },
  bought_for_him: { label: 'Bought for him',    color: '#276b3c' },
  // The job centre (sim-server world/JobCentre.js): jobs he put up, and a picture somebody did one for him with, his
  // line about it under their picture.
  put_up:         { label: 'Put up jobs',       color: '#1e5a46' },
  job_done:       { label: 'A job done for him', color: '#1e5a46' },
  sold_stickers:  { label: 'Stickers sold',     color: '#276b3c' },
  ran_disk:       { label: 'Ran a disk',        color: '#175f83' },
  painted:     { label: 'Drew something', color: '#9c3568' },
  // The workshop's beats, act two.
  opened:      { label: 'Doors open',      color: '#276b3c' },
  started_print: { label: 'Set it going',  color: '#3d72b8' },
  printing:    { label: 'Mid-print',       color: '#175f83' },
  printed:     { label: 'Printed it',      color: '#276b3c' },
  misprint:    { label: 'Misprint',        color: '#a83832' },
  no_filament: { label: 'Out of filament', color: '#8a5a12' },
  first_notice: { label: 'Something new',  color: '#5f42ab' },
  peered:      { label: 'Had a look',      color: '#57506e' },
}

const CATEGORY_COLORS = {
  'Learned Facts':      '#8a5a12',
  'Important Memories': '#5f42ab',
  'Relationships':      '#9c3568',
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// His lines carry real URLs now (the hobby pages he puts up), and a URL
// you cannot click is a wall. Split-and-map, never innerHTML: the text is
// model-written and only ever rendered as text. http(s) only.
function Linkified({ text }) {
  const str = String(text || '')
  const parts = str.split(/(https?:\/\/[^\s"']+[^\s"'.,;:!?)])/g)
  if (parts.length === 1) return str
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="underline decoration-dashed underline-offset-2 break-all hover:opacity-70"
        onClick={(e) => e.stopPropagation()}
      >
        {part.replace(/^https?:\/\//, '')}
      </a>
    ) : (
      part
    )
  )
}

function relativeTime(ts) {
  const diff = Date.now() - ts
  if (diff < 60000)   return 'just now'
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
  if (diff < 86400000)return `${Math.floor(diff / 3600000)}h ago`
  return new Date(ts).toLocaleDateString()
}

// Readable on cream. The old scale was tuned for a black panel, where mid
// yellow reads fine; against #f7e7d2 it measured 1.38:1, which is to say
// invisible. These clear 4.5:1, checked rather than eyeballed.
function moodColor(valence) {
  if (valence >  0.3) return '#276b3c'
  if (valence > -0.1) return '#8a5a12'
  return '#a83832'
}

function moodLabel(valence) {
  if (valence >  0.5) return 'elated'
  if (valence >  0.2) return 'content'
  if (valence > -0.1) return 'neutral'
  if (valence > -0.4) return 'low'
  return 'distressed'
}

// ── Sub-components ────────────────────────────────────────────────────────────

function MoodArc({ entries }) {
  const points = entries
    .filter(e => e.mood?.valence !== undefined)
    .slice(0, 20)
    .reverse()

  if (points.length < 2) return null

  const W = 200, H = 36, PAD = 4
  const innerW = W - PAD * 2
  const innerH = H - PAD * 2

  const coords = points.map((e, i) => {
    const x = PAD + (i / (points.length - 1)) * innerW
    const y = PAD + ((1 - (e.mood.valence + 1) / 2) * innerH)
    return [x, y]
  })

  const path = coords.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')
  const latest = points[points.length - 1].mood.valence
  const strokeColor = moodColor(latest)

  return (
    <div className="w-full flex flex-col gap-1 mb-3">
      <div className="flex justify-between items-center">
        <span className="text-[11px] tracking-[0.02em] font-bold text-[#2f2650]/60">Mood Arc</span>
        <span className="text-[11px]" style={{ color: strokeColor }}>
          {moodLabel(latest)}
        </span>
      </div>
      <svg width={W} height={H} className="w-full" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        <line x1={PAD} y1={H / 2} x2={W - PAD} y2={H / 2} stroke="rgba(47,38,80,0.16)" strokeWidth="1" />
        <path d={path} fill="none" stroke={strokeColor} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={coords[coords.length - 1][0]} cy={coords[coords.length - 1][1]} r="2.5" fill={strokeColor} />
      </svg>
    </div>
  )
}


/** A drawn heart, because no emoji anywhere in this product. */
function HeartMark({ filled }) {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true" style={{ display: 'block' }}>
      <path
        d="M8 13.2 3.4 8.8a2.9 2.9 0 0 1 0-4.1 2.8 2.8 0 0 1 4 0l.6.6.6-.6a2.8 2.8 0 0 1 4 0 2.9 2.9 0 0 1 0 4.1Z"
        fill={filled ? '#c2586f' : 'none'} stroke="#c2586f" strokeWidth="1.5" strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * The heart on a diary entry. Counts arrive on the entry itself (the sim
 * folds them into /api/journal); pressing goes through the signed-in
 * route, one per person per entry, enforced server-side. localStorage
 * only remembers which ones THIS browser pressed, so the filled state
 * survives a refresh without a second fetch.
 *
 * Deliberately nothing here reaches him. The hearts are the readers';
 * him finding out one day is its own beat, the hobby-page way, where he
 * chose whether to know.
 */
function EntryHeart({ entry }) {
  const { signedIn } = useAccount()
  // No effects: the server count rides the entry prop (the poll refreshes
  // it), a press only ever raises it, and the pressed flag lazy-reads
  // localStorage once per mount. Math.max keeps the freshest of the two
  // without a sync effect the compiler rules flag.
  const [pressedCount, setPressedCount] = useState(0)
  const [mine, setMine] = useState(() => {
    try { return !!JSON.parse(localStorage.getItem('pino-journal-hearts') || '{}')[entry.ts] } catch { return false }
  })
  const [note, setNote] = useState(null)
  const count = Math.max(entry.likes || 0, pressedCount)
  const press = async () => {
    if (mine) return
    if (!signedIn) { setNote('sign in to leave one'); return }
    try {
      const res = await authFetch('/api/journal/like', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ts: entry.ts }),
      })
      const d = await res.json().catch(() => ({}))
      if (res.status === 401) { setNote('sign in to leave one'); return }
      // silence on failure reads as a broken button, so say something
      if (!res.ok) { setNote('did not land, try again'); return }
      setNote(null)
      if (typeof d.count === 'number') setPressedCount(d.count)
      setMine(true)
      try {
        const seen = JSON.parse(localStorage.getItem('pino-journal-hearts') || '{}')
        seen[entry.ts] = 1
        localStorage.setItem('pino-journal-hearts', JSON.stringify(seen))
      } catch { /* pressed all the same */ }
    } catch { setNote('did not land, try again') }
  }
  return (
    <span className="flex items-center gap-1 shrink-0">
      {note && <span className="text-[10px] text-[#2f2650]/50">{note}</span>}
      <button
        type="button"
        onClick={press}
        aria-label={mine ? 'you left a heart on this entry' : 'leave a heart on this entry'}
        title={mine ? 'you left a heart' : 'leave a heart'}
        className="flex items-center gap-1"
        style={{ cursor: mine ? 'default' : 'pointer', opacity: mine || count ? 1 : 0.5, padding: 0, background: 'none', border: 'none' }}
      >
        <HeartMark filled={mine} />
        {count > 0 && <span className="text-[10.5px] font-bold" style={{ color: '#c2586f' }}>{count}</span>}
      </button>
    </span>
  )
}

function MemoryEntry({ entry }) {
  const color = CATEGORY_COLORS[entry.category] || kawaii.ink
  return (
    <div className="flex flex-col gap-1 border-l-2 pl-2" style={{ borderColor: color + '60' }}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] tracking-[0.02em] font-bold" style={{ color }}>
          {entry.category}
        </span>
        <span className="flex items-center gap-2">
          <EntryHeart entry={entry} />
          <span className="text-[11px] text-[#2f2650]/60">{relativeTime(entry.ts)}</span>
        </span>
      </div>
      <p className="text-[13.5px] text-[#2f2650] leading-relaxed">
        {entry.content}
      </p>
    </div>
  )
}

function ActivityEntry({ entry, strand = null, onStrand = null }) {
  // Unknown outcomes read as words, never as a raw snake_case key: the
  // narrator grows beats faster than this table grows rows.
  // A verdict's heading carries the facts the old template sentence did
  // (kept or let go, a note or a picture), so the line itself can be his
  // words alone. The "from X" chip carries the who.
  const outstyle = entry.outcome === 'sacrifice_judged'
    ? { label: `${entry.verdict === 'accept' ? 'Kept' : 'Let go'}, ${entry.kind === 'image' ? 'a picture' : 'a note'}`, color: entry.verdict === 'accept' ? '#5f42ab' : '#8a5a12' }
    // a sitting at the shrine (30 Oct): a pile read in one go, one line for it, how many read and kept in the heading
    : entry.outcome === 'shrine_sitting' && Number.isFinite(entry.read)
      ? { label: `Read ${entry.read} ${entry.read === 1 ? 'note' : 'notes'} at the shrine, kept ${entry.kept ?? 0}`, color: '#5f42ab' }
    : entry.outcome === 'talked_to_friend' && entry.friend
      ? { label: `Talked to ${entry.friend}`, color: OUTCOME_STYLES.talked_to_friend.color }
      : entry.outcome === 'tended_friend' && entry.friend
        ? { label: `Rewrote ${entry.friend}`, color: OUTCOME_STYLES.tended_friend.color }
        : entry.outcome === 'friend_left_note' && entry.friend
        ? { label: `A note from ${entry.friend}`, color: OUTCOME_STYLES.friend_left_note.color }
        : OUTCOME_STYLES[entry.outcome]
    || { label: String(entry.outcome || '').replace(/_/g, ' '), color: kawaii.ink }
  // ACTIVITY_LABELS is keyed by activity, but the narrator's beats are keyed
  // by PLACE, and the two vocabularies only overlap by luck ("pond" is in
  // both, "surfInternet" is in neither). A found deed used to file itself
  // under "walk", which told a reader nothing; filing it under the place
  // told them "surfInternet". The places already carry English titles for
  // the actions popup, so that is the second lookup rather than a third
  // list to keep in step.
  // How he IS beats what the entry is filed under. A first-time reader
  // met "Gift / wanting" on a line about being cold and hungry, which
  // named the machinery and not the bird. When he is in a state, the
  // state is the heading and the filing disappears.
  const kindLabel = entry.state
    ? entry.state.charAt(0).toUpperCase() + entry.state.slice(1)
    : labelFor(entry.kind)
  const who = <Giver entry={entry} />
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-[11px] tracking-[0.02em] font-bold text-[#2f2650]/60">{kindLabel}</span>
          {!entry.state && (
            <span className="text-[11px] px-1 rounded-sm" style={{ color: outstyle.color, border: `1px solid ${outstyle.color}50` }}>
              {outstyle.label}
            </span>
          )}
          {who}
        </div>
        <span className="flex items-center gap-2">
          <EntryHeart entry={entry} />
          <span className="text-[11px] text-[#2f2650]/60">{relativeTime(entry.ts)}</span>
        </span>
      </div>
      {/* Name the film. This block used to check movieTitle and then render
          the SYNOPSIS, so a reader got a paragraph about a thirteen-year-old
          witch with nothing saying it was a film, let alone which one. The
          title was sitting in the entry the whole time, unused. */}
      {entry.movieTitle && (
        <p className="text-[12.5px] font-bold text-[#2f2650]">{entry.movieTitle}</p>
      )}
      {/* Internet entries carry where he was reading as data, the same way
          films carry their title. It used to be a canned sentence welded
          onto the front of his thought ("Followed a link to..."), which
          was the most repeated phrase in the whole diary. */}
      {/* A question he is looking into across sittings says so above the
          page, with which look this is, so "the third time he has looked
          into this" is readable without the rail. */}
      {entry.kind === 'internet' && entry.quest?.question && entry.quest.look > 1 && (
        <p className="text-[11.5px] font-semibold text-[#5f42ab]">
          {`still looking into: ${entry.quest.question} (look ${entry.quest.look})`}
        </p>
      )}
      {entry.kind === 'internet' && entry.title && (
        <p className="text-[11.5px] font-semibold text-[#2f2650]/55">
          {entry.query ? `looked up "${entry.query}"${entry.why ? `, ${entry.why}` : ''} · ` : ''}reading: {entry.title}
        </p>
      )}
      {entry.outcome === 'talked_to_friend' && Array.isArray(entry.said) && entry.said.length
        ? <FriendTalk entry={entry} />
        : entry.outcome === 'friend_left_note'
          ? <FriendNote entry={entry} />
          : <p className="text-[13.5px] text-[#2f2650] leading-relaxed"><Linkified text={entry.detail} /></p>}
      {/* a job somebody did for him at the job centre: their picture, what the job was, and who */}
      {entry.kind === 'jobs' && entry.outcome === 'job_done' && entry.imageUrl && (
        <figure className="m-0">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={entry.imageUrl} alt={entry.what || 'a picture somebody sent him'} loading="lazy" className="w-full max-h-72 object-cover rounded-xl border-2 border-[#2f2650]" />
          <figcaption className="text-[11.5px] font-semibold text-[#2f2650]/60 mt-1">{entry.what ? `${String(entry.what).replace(/\.$/, '')}, ` : ''}by {entry.handle || 'somebody'}</figcaption>
        </figure>
      )}
      {/* His reason as he chose it, in his words, where the line does not
          already say it (the town decides that: reasonToShow). Sam, 27 Oct:
          "any user should be able to see linked actions - he did this
          because of this". Read cold, the day tab said why in an earlier
          entry once in sixty. */}
      {entry.because && (
        <p className="text-[12px] leading-snug text-[#5f42ab]">
          <span className="font-semibold">his reason:</span> {entry.because}
        </p>
      )}
      {/* One step of a longer story (the stories tab, the town's
          world/Strands.js): said here, so a visitor reading the day can tell
          this belongs to something he has been at for a while, and go to the
          whole of it (Sam, 27 Oct: "threads that go on for a long time"). */}
      {strand && (
        <button type="button" onClick={onStrand || undefined} className="self-start text-left text-[11.5px] font-semibold underline" style={{ color: '#5f42ab' }}>
          {`part of a longer story: ${strand.title}`}
        </button>
      )}
      {(entry.outcome === 'made_friend' || entry.outcome === 'friend_started') && entry.friend && (
        <a href="/friend" className="text-[11.5px] font-semibold underline" style={{ color: '#9c3568' }}>{`what he wrote ${entry.friend} to be`}</a>
      )}
      {entry.synopsis && (
        <p className="text-[12px] italic text-[#2f2650]/60">{entry.synopsis}</p>
      )}
    </div>
  )
}

// The cafe computer's screen, and its face in the corner, as on /friend
const SCREEN = { background: '#1c1715', color: '#f7e7d2', borderRadius: 10, fontFamily: 'var(--font-geist-mono), ui-monospace, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }

// A talk with the friend he made on the cafe computer: the first few
// lines as they were said, in the cafe computer's own face, and the way
// to the rest. His first line is the entry's detail, so it is not said
// twice.
function FriendTalk({ entry }) {
  const who = String(entry.friend || 'it').toLowerCase().replace(/\s+/g, '-')
  return (
    <div className="flex flex-col gap-1">
      <div className="px-2.5 py-2 text-[12px] leading-relaxed flex gap-2.5 items-start" style={SCREEN}>
        <FriendFace face="awake" size={30} color="#f7e7d2" marks="#f0b064" title={`${entry.friend}'s face`} />
        <div className="min-w-0">
          {entry.said.map((l, i) => (
            <div key={i}>
              <span style={{ color: l.by === 'pino' ? '#a8d6a0' : '#f0b064' }}>{l.by === 'pino' ? 'pino' : who}&gt;</span> {l.text}
            </div>
          ))}
        </div>
      </div>
      {entry.lookedUp?.length > 0 && (
        <p className="text-[11.5px] font-semibold text-[#2f2650]/55">{`told it what he found out about: ${entry.lookedUp.join('; ')}`}</p>
      )}
      <a href={`/friend#${entry.talk || ''}`} className="text-[11.5px] font-semibold underline" style={{ color: '#9c3568' }}>the whole talk</a>
    </div>
  )
}

// A note it left on the screen for him while he was away, in the screen's face.
function FriendNote({ entry }) {
  const who = String(entry.friend || 'it').toLowerCase().replace(/\s+/g, '-')
  return (
    <div className="px-2.5 py-2 text-[12px] leading-relaxed flex gap-2.5 items-start" style={SCREEN}>
      <FriendFace face="curious" size={30} color="#f7e7d2" marks="#f0b064" title={`${entry.friend}'s face`} />
      <div className="min-w-0">
        <span style={{ color: '#f0b064' }}>{who} (note)&gt;</span> {entry.detail}
      </div>
    </div>
  )
}

// The lines a visitor caused wear that visitor's name. A verdict, a
// second look, a deed he found: the entry already carries the handle,
// and the card used to keep it in the prose where a reader skimming for
// "can I change anything here" never saw it. When it is you, it says so.
const GIVER_OUTCOMES = {
  sacrifice_judged: 'from',
  revisited_offering: 'from',
  released_offering: 'from',
  noticed_something: 'left by',
  wrote_email: 'to',
  answered_talk: 'to',
  told_them: 'to',
  sold_print: 'to',
  posted_print: 'to',
  bought_for_him: 'from',
  sold_stickers: 'to',
}
function Giver({ entry }) {
  const { username, called } = useAccount()
  const verb = GIVER_OUTCOMES[entry.outcome]
  // A thread has several people in it; they ride the entry as `others`.
  const names = entry.handle ? [entry.handle] : (Array.isArray(entry.others) ? entry.others : [])
  if (!verb || !names.length) return null
  const mine = [username, called].filter(Boolean).map((n) => String(n).toLowerCase())
  const you = names.some((n) => mine.includes(String(n).toLowerCase()))
  const handle = you && names.length > 1
    ? `you and ${names.length - 1} ${names.length === 2 ? 'other' : 'others'}`
    : names.slice(0, 3).join(', ') + (names.length > 3 ? ` and ${names.length - 3} more` : '')
  return (
    <span
      className="text-[11px] px-1.5 rounded-full truncate"
      style={{
        color: you ? '#8a5a12' : '#5f42ab',
        background: you ? '#f5e3b8' : '#ebe3fb',
        border: `1px solid ${you ? '#8a5a12' : '#5f42ab'}40`,
        fontWeight: 800,
        maxWidth: 150,
      }}
      title={you ? 'this one is yours' : `${handle} did this`}
    >
      {verb} {you && names.length === 1 ? 'you' : handle}
    </span>
  )
}

/**
 * A story: the entry that just happened, with the trail that led to it.
 *
 * Sam: "when a new entry is added, if it is part of a trail of thought,
 * we show it with the previous actions too... this will read as narrative
 * in his day." The ancestors stack above, minimised to a timestamp, a
 * link mark and one line; the rail joins them; the newest entry sits full
 * size at the bottom as the thing being explained. A trail that starts
 * beyond the loaded window opens with a stub carrying the cause's label
 * and words, so a thought from days ago still heads its own story.
 */
function StoryGroup({ ancestors, head, children }) {
  // The join between two rows carries the CLAIM: the following entry's
  // cause label ("what he saw at the museum"), rendered small on the
  // rail. Sam's screenshots showed the failure this fixes: a museum
  // moment stacked over a graffiti sitting with nothing visible joining
  // them reads as random association, which is worse than no link at
  // all, because it spends the trust every honest rail earned. A join
  // that states its reason can be judged; a bare line can only be
  // doubted. Stubs already carry their label as their heading, so only
  // entry-to-entry joins need the connective said out loud.
  const joinFor = (i) => {
    const next = i + 1 < ancestors.length ? ancestors[i + 1] : { entry: head }
    if (!next.entry) return null
    return next.entry.cause?.label || null
  }
  return (
    <div className="flex flex-col">
      {ancestors.map((a, i) => (
        <MiniEntry key={i} node={a} first={i === 0} joinBelow={a.entry ? joinFor(i) : null} />
      ))}
      <div className="flex gap-2.5">
        <div className="flex flex-col items-center shrink-0" style={{ width: 14 }} aria-hidden="true">
          <div style={{ width: 2, height: 8, background: '#7a63c8' }} />
          <LinkMark filled />
        </div>
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </div>
  )
}

/**
 * The join, in words a person would use. "then, because of this:
 * something he read" was machine grammar: the labels are noun phrases
 * built for stub HEADINGS, and jamming them after a colon made every
 * connective read stilted, which Sam heard immediately. Each cause class
 * gets a phrase that reads as prose between two diary rows; the fallback
 * covers every mined label ("with this still on his mind" works after
 * the museum, the film, the green stone, a letter) without naming it.
 */
function joinPhrase(label) {
  const exact = {
    'the moment it grew from': 'a memory grew out of this',
    'something he had been meaning to do': 'he finally got to it',
    'the thread he was pulling': 'he kept pulling the thread',
    'what the day kept returning to': 'the day kept coming back to it',
    'a plan he made': 'he had said he would go',
    'a plan he let slip': 'and he let the plan slip',
    'the all nighter': 'the night collected its bill',
    'earlier today': 'still on his mind later',
    'what he slept on': 'he slept on it',
    // The searches, since 23 Sep: a question kept across sittings, a thing
    // he asked the world for, a dream, and the questions his own projects
    // raise. Each is a plain join so the rail reads as prose.
    'the thing he keeps looking into': 'he went back to it',
    'the thing he asked for at the shrine': 'he had asked the world for it',
    'something he dreamt': 'he dreamt it first',
    'the tune he keeps playing at the piano': 'the tune was still in his head',
    'the prints he has up for sale': 'the prints were on his mind',
    'the torn map': 'the map was on his mind',
    'the greenhouse beds': 'the beds were on his mind',
    'the thing on his shelf': 'the thing on his shelf was on his mind',
    'the thing he is making': 'what he is making was on his mind',
    // A night he stayed up, from the sitting that kept him there (the
    // town's world/Nights.js, 27 Oct).
    'what kept him up': 'he kept at it past bedtime',
    // A stop on his way somewhere, and getting there after it (the town's
    // world/Passing.js, 27 Oct).
    'where he was going before': 'then he carried on',
  }
  if (exact[label]) return exact[label]
  // Aftereffects: the meal, the drink, the casino, the mushrooms, the
  // dancing. All of them are "this caught up with him" joins. A mood
  // shift his mind puts down to something he had is labelled "after the
  // flat white" (the town's world/MindMoods.js, 27 Oct).
  if (/^the (coffee|burger|noodles|chips|drink|mushrooms|casino|dancing)$/.test(label) || /^after the /.test(label)) return 'it caught up with him'
  // The giver stays named on the rail. "what they left" was the one place
  // the diary knew exactly who had moved him and said "they".
  let m = /^what (.+) left him$/.exec(label)
  if (m) return `what ${m[1]} left stayed with him`
  if (/^on his way to /.test(label)) return 'he stopped on the way'
  // A mood shift his mind put down to somebody, hung off what they said
  // (the town's world/MindMoods.js, 27 Oct).
  m = /^what (.+) said$/.exec(label)
  if (m) return `what ${m[1]} said stayed with him`
  m = /^the note (.+) left at the shrine$/.exec(label)
  if (m) return `${m[1]}'s note was still with him`
  m = /^a question (.+) left at the shrine$/.exec(label)
  if (m) return `${m[1]}'s question was still with him`
  if (/^the (poster|notice) at /.test(label)) return 'he saw it coming'
  if (/^what followed him from /.test(label)) return 'it followed him here'
  return 'with this still on his mind'
}

function MiniEntry({ node, first, joinBelow = null }) {
  const e = node.entry
  const stub = node.stub
  const text = e
    ? (e.detail || e.reason || e.content || '')
    : (stub.excerpt || '')
  const when = e ? relativeTime(e.ts) : (stub.ts ? relativeTime(stub.ts) : 'a while back')
  const head = e
    ? (e.state ? e.state.charAt(0).toUpperCase() + e.state.slice(1) : labelFor(e.kind || e.type))
    : stub.label
  return (
    <div className="flex gap-2.5">
      <div className="flex flex-col items-center shrink-0" style={{ width: 14 }} aria-hidden="true">
        <div style={{ width: 2, height: first ? 4 : 8, background: first ? 'transparent' : '#7a63c8' }} />
        <LinkMark filled={!first} />
        <div style={{ width: 2, flex: 1, minHeight: 8, background: '#7a63c8' }} />
      </div>
      <div className="min-w-0 flex-1 pb-2.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[10.5px] tracking-[0.03em] font-bold" style={{ color: '#5f42ab' }}>{head}</span>
          <span className="text-[10.5px] shrink-0 text-[#2f2650]/50">{when}</span>
        </div>
        {/* What he was reading, when the step was a page. "The
            jazz-as-authentic bit made me grin" is a fine line and an
            unanswerable one without the page it came from: the title is
            on the entry, and hiding it in a minimised row made the reader
            do archaeology the row already knew. */}
        {e?.title && e.kind === 'internet' && (
          <p
            className="text-[11px] font-bold text-[#2f2650]/60"
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={e.title}
          >
            reading: {e.title}
          </p>
        )}
        {/* Three lines, not one. The single-line clamp made the thread
            illegible: Sam read "finally the right stu..." and could not
            derive what the thought even was, which defeats a view whose
            entire purpose is showing the thought. Three lines holds a
            whole short entry and clips only the long ones, with the full
            text still a hover away. */}
        {text && (
          <p
            className="text-[12px] leading-snug text-[#2f2650]/75"
            style={{
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}
            title={text}
          >
            {text}
          </p>
        )}
        {joinBelow && (
          <p className="text-[10.5px] italic" style={{ color: '#7a63c8', margin: '4px 0 0' }}>
            {joinPhrase(joinBelow)}
          </p>
        )}
      </div>
    </div>
  )
}

/** Two links of a chain. Drawn, because no emoji anywhere in this product. */
function LinkMark({ filled }) {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" style={{ flex: 'none', display: 'block' }}>
      <g fill="none" stroke="#7a63c8" strokeWidth="1.6" strokeLinecap="round">
        <path d="M5.4 8.6 8.6 5.4" />
        <path d="M8.1 3.6a2.2 2.2 0 0 1 3.1 3.1l-1.3 1.3" />
        <path d="M5.9 10.4a2.2 2.2 0 0 1-3.1-3.1l1.3-1.3" />
      </g>
      {filled && <circle cx="7" cy="7" r="1.1" fill="#7a63c8" />}
    </svg>
  )
}

function MoodEventEntry({ entry }) {
  const valDelta = entry.delta?.valence || 0
  const arrow = valDelta > 0.05 ? '↑' : valDelta < -0.05 ? '↓' : '→'
  const col = valDelta > 0 ? '#276b3c' : valDelta < 0 ? '#a83832' : '#8a5a12'
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <span className="font-mono text-sm leading-none" style={{ color: col }}>{arrow}</span>
          <span className="text-[11px] tracking-[0.02em] font-bold text-[#2f2650]/60">Mood shift</span>
        </div>
        <span className="text-[11px] text-[#2f2650]/60">{relativeTime(entry.ts)}</span>
      </div>
      <p className="text-[13.5px] text-[#2f2650] leading-relaxed italic">"{entry.reason}"</p>
    </div>
  )
}

// Types we render as journal cards. Anything else gets filtered out before
// the map so we don't emit empty padded wrapper divs for it. Nothing is
// deleted by this; it all still lives in journal.jsonl, and /admin shows
// the unfiltered feed.
//
// 'completion' is out because those are the raw "ate at X" lines.
//
// 'decision' is out for a better reason. Decision reasons answer "why am I
// doing this", and measured against the moment narrator, which asks what he
// noticed, that question scores -0.87 to 0.01 on the voice scorer over ~12
// samples each. Same model, same persona, same situation. A justification
// only has one honest shape ("because X"), and no amount of prompt work
// makes a justification sound like a person; every riddle Sam has flagged
// ("hoping the edge will speak up", "need to get close to the stone and
// listen for its whisper") came from this field. So the diary is now what
// he noticed and what he kept, and the reasoning moved to /mind where it
// reads as instrumentation rather than as his voice.
//
// Since 8 October the diary is his own entries, written by the mind's diary writer (one source of truth, the pino
// brain: pino-protocol src/pens/Pens.js). The narrator was the town writing in his name and is off while the mind
// is on. His thinking lines ('decision') stay on /mind, as they always did here: a reason for doing a thing reads
// as a justification, and a diary of them read "Legs ache, need a seat" four times an hour.
// (Phase 0, 8 Oct: the town's writers are back, and the diary is as it was. the mind's own diary entries are written
// on the side for now, to be read against these before anything moves)
const RENDERED_TYPES = new Set(['memory', 'activity', 'mood_event', 'wish'])
const ONE_MIND_SINCE = Date.parse('2026-10-08T06:21:00Z')

// Outcomes that mean a page didn't load or didn't read. Sam has asked twice
// for these to stop appearing: they were 7% of the diary, they carry raw
// URLs, and "the page barely had anything to read, half a sentence and a
// login form" turned up twice word for word. A failed fetch is a fact about
// the internet, not a moment in his life.
//
// They stay in journal.jsonl and on /admin, because when he stops being able
// to read anything that IS the thing you need to see. CLAUDE.md already
// keeps them out of the daily log so they can never reach MEMORY.md.
const FAILURE_OUTCOMES = new Set(['failed', 'unreadable', 'thin', 'flat'])

// Session bookkeeping that the diary tells better elsewhere. The 'started'
// row was a two-shape template forever ("searched for X, landed on Y") and
// said nothing about why; the first 'read' entry now carries how he got
// there and what sent him, so showing both is showing the same event twice.
// Stays in journal.jsonl and on /admin like the failures above.
const INTERNAL_OUTCOMES = new Set(['started'])

function JournalEntry({ entry, strand = null, onStrand = null }) {
  switch (entry.type) {
    case 'memory':     return <MemoryEntry entry={entry} />
    case 'activity':   return <ActivityEntry entry={entry} strand={strand} onStrand={onStrand} />
    case 'mood_event': return <MoodEventEntry entry={entry} />
    case 'wish':       return <WishEntry entry={entry} />
    case 'diary':      return <DiaryEntry entry={entry} />
    default:           return null
  }
}

// An entry in his diary, as the mind wrote it.
function DiaryEntry({ entry }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-[11px] tracking-[0.02em] font-bold text-[#2f2650]/60">Diary</span>
        <span className="flex items-center gap-2">
          <EntryHeart entry={entry} />
          <span className="text-[11px] text-[#2f2650]/60">{relativeTime(entry.ts)}</span>
        </span>
      </div>
      <p className="text-[13.5px] text-[#2f2650] leading-relaxed">{entry.text}</p>
    </div>
  )
}

// One of his own lines, as he said it while choosing what to do. Headed by what he was doing, in a word.
// (not in the diary since 8 October; kept for /admin's use)
const DOING_WORDS = { forage: 'Food', rest: 'Roost', inspect: 'Looking', move: 'Walking', move_to: 'Walking', wait: 'Still', play_piano: 'Piano', jam: 'Synth', visit_studio: 'Studio', paddle: 'Pond', browse_internet: 'Internet', judge_offering: 'Shrine', ask_at_shrine: 'Shrine', speak: 'Said', post_to_twitter: 'Posted', watch_movie: 'Cinema', gamble: 'Casino', go_rave: 'Rave', watch_laundry: 'Laundrette', visit_museum: 'Museum', use_phone: 'Payphone', survey: 'Lookout', scavenge: 'Junk heap' }
function ThoughtEntry({ entry }) {
  const verb = String(entry.action || '').toLowerCase()
  const plain = verb.replace(/_/g, ' ')
  const heading = DOING_WORDS[verb] || (plain ? plain.charAt(0).toUpperCase() + plain.slice(1) : 'Thinking')
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-[11px] tracking-[0.02em] font-bold text-[#2f2650]/60">{heading}</span>
        <span className="flex items-center gap-2">
          <EntryHeart entry={entry} />
          <span className="text-[11px] text-[#2f2650]/60">{relativeTime(entry.ts)}</span>
        </span>
      </div>
      <p className="text-[13.5px] text-[#2f2650] leading-relaxed">{entry.reason}</p>
    </div>
  )
}

// A wish said to a visitor (there are eight, ever, so this card can
// afford to stand out a little). The count is the drama: 3 of 8 tells a
// reader the shape of the whole arc in two numbers.
function WishEntry({ entry }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] tracking-[0.02em] font-bold" style={{ color: '#7c5cbf' }}>a wish</span>
          {entry.n && entry.total && (
            <span className="text-[11px] px-1 rounded-sm" style={{ color: '#7c5cbf', border: '1px solid #7c5cbf50' }}>
              {entry.n} of {entry.total}
            </span>
          )}
        </div>
        <span className="text-[11px] text-[#2f2650]/60">{relativeTime(entry.ts)}</span>
      </div>
      <p className="text-[13.5px] text-[#2f2650] leading-relaxed">"<Linkified text={entry.detail} />"</p>
    </div>
  )
}

// What he has asked the world for, standing at the shrine.
//
// A tab rather than a page of its own: Sam, on /wants, "people won't go
// to it". This is where anybody already is when they want to know what
// he is doing, so it is where the asking belongs.
//
// The REASON leads, not the request. An ask on its own is a wish list
// and anybody can make one of those; the thing worth reading is that
// each points at something that actually happened to him, and he
// cannot ask without one.
const ASK_LOOK = {
  open: { label: 'waiting', tone: kawaii.ink },
  granted: { label: 'done', tone: '#3f7a35' },
  declined: { label: 'no', tone: kawaii.inkSoft },
  later: { label: 'not yet', tone: kawaii.inkSoft },
}

function Wants() {
  const [data, setData] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let dead = false
    fetch('/api/asks', { cache: 'no-store' })
      .then((res) => res.ok ? res.json() : null)
      .then((d) => { if (!dead) { if (d) setData(d); else setFailed(true) } })
      .catch(() => { if (!dead) setFailed(true) })
    return () => { dead = true }
  }, [])

  const quiet = { color: kawaii.inkSoft, fontFamily: ui.font, fontSize: 13, fontWeight: 650, lineHeight: 1.55, padding: '10px 2px' }
  if (!data && !failed) return <div style={quiet}>reading the shrine...</div>
  if (failed) return <div style={quiet}>the shrine is not answering just now.</div>

  const list = data.list || []
  if (!list.length) {
    return (
      <div style={quiet}>
        He has not asked for anything yet. He asks at the cat shrine, where everything anybody
        has given him is set out, and he needs a reason first. A reason has to be something that
        really happened to him.
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p style={{ ...quiet, padding: '2px 2px 4px' }}>
        He asks at the cat shrine, where everything people have given him is set out. Every one of
        these points at something that actually happened to him.
      </p>
      {list.map((a) => {
        const look = ASK_LOOK[a.status] || ASK_LOOK.open
        return (
          <div
            key={a.id}
            style={{
              border: kawaii.outlineThin,
              borderRadius: 14,
              background: kawaii.cream,
              padding: '11px 13px',
              fontFamily: ui.font,
            }}
          >
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <span style={{ fontSize: 14, fontWeight: 800, color: kawaii.ink, lineHeight: 1.4 }}>{a.ask}</span>
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 800,
                  color: look.tone,
                  border: `2px solid ${look.tone}`,
                  borderRadius: 999,
                  padding: '1px 8px',
                  whiteSpace: 'nowrap',
                }}
              >
                {look.label}
              </span>
            </div>
            {a.why ? (
              <p style={{ fontSize: 12.5, fontWeight: 650, color: kawaii.ink, margin: '7px 0 0', lineHeight: 1.5 }}>{a.why}</p>
            ) : null}
            {a.because ? (
              <p style={{ fontSize: 11.5, fontWeight: 650, color: kawaii.inkSoft, margin: '6px 0 0', lineHeight: 1.5 }}>
                what brought it on: {a.because}
              </p>
            ) : null}
            {a.note ? (
              <p style={{ fontSize: 12, fontWeight: 800, color: kawaii.ink, margin: '8px 0 0', lineHeight: 1.5 }}>
                he was told: {a.note}
              </p>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

// ── The stories tab ───────────────────────────────────────────────────────────
//
// What is still moving in his life, as sentences with their causes. It
// used to open on three bare labels ("what he means to do"), then tiles of
// counters of sittings at each skill, then each story
// with a count of turns and tests under it and a moth-room crack filed
// under the piano. A counter is a readout of the machine; the numbers live
// on /brain now. Here: what he is doing and why, what he means to do and
// who asked, the question he is carrying and who left it, what changed
// lately. Then the stories, each as what happened, in order, with the
// person when a visitor set it off. Then what people set off, and what he
// thinks he knows, as plain sentences.

const STAGE_WORDS = {
  starting: 'just started',
  pursuing: 'still going',
  reconsidering: 'he is thinking again about it',
  resolved: 'settled',
  abandoned: 'he let it go',
  quiet: 'gone quiet for now',
}

function lowerFirst(s) {
  const t = String(s || '').trim()
  return t ? t.charAt(0).toLowerCase() + t.slice(1) : t
}

function clip(s, n) {
  const t = String(s || '').trim()
  if (t.length <= n) return t
  const cut = t.slice(0, n)
  const at = cut.lastIndexOf(' ')
  return `${(at > n * 0.6 ? cut.slice(0, at) : cut).replace(/[,;:]$/, '')}...`
}

function storyEyebrow(story) {
  // strands (the town's world/Strands.js, 27 Oct): what started them is said under the title
  if (story.kind === 'asked') return `something ${story.started?.who || 'somebody'} asked of him`
  if (story.kind === 'looking') return 'something he keeps looking into'
  if (story.kind === 'making') return 'a piece he is making'
  if (story.kind === 'quest') {
    if (story.looks >= 3) return 'a question he has looked into three times'
    if (story.looks === 2) return 'a question he went back to'
    return 'a question he is looking into'
  }
  if (story.kind === 'project') return story.sparkedBy ? `a thing of his own, ${story.sparkedBy}'s idea really` : 'a thing of his own'
  return 'something he keeps going back to'
}

function wantSentence(want) {
  const text = lowerFirst(want.text).replace(/[.!?]+$/, '')
  return want.from
    ? `He means to ${text}, after what ${want.from} left at the shrine.`
    : `He means to ${text}, something he told himself he would do.`
}

function questionSentence(question) {
  const text = String(question.text || '').trim()
  return question.from
    ? `${question.from} asked him something he has not answered yet: "${text}"`
    : `Something he keeps wondering: "${text}"`
}

function influenceSentence(influence) {
  const who = influence.person
  const line = clip(influence.consequence || influence.latest || influence.source, 150)
  if (influence.stage === 'changed_something') return `${who} changed something. ${line}`
  if (influence.stage === 'acted_on') return `${who} set something off. ${line}`
  if (influence.stage === 'kept') return `He kept what ${who} left. ${line}`
  // A note, a picture, a deed done at a place: all things they did.
  return `Something ${who} did reached him: "${clip(influence.source || influence.latest, 150)}"`
}

const INFLUENCE_RANK = { changed_something: 3, acted_on: 2, kept: 1, received: 0 }

function Eyebrow({ children }) {
  return <p style={{ color: '#5f42ab', fontFamily: ui.font, fontSize: 12, fontWeight: 850 }}>{children}</p>
}

function Line({ eyebrow, when, children }) {
  return (
    <div>
      <Eyebrow>{eyebrow}</Eyebrow>
      <p style={{ color: kawaii.ink, fontFamily: ui.font, fontSize: 13.5, fontWeight: 650, lineHeight: 1.45, marginTop: 1 }}>{children}</p>
      {when && (
        <p style={{ color: kawaii.inkSoft, fontFamily: ui.font, fontSize: 12, fontWeight: 700, marginTop: 2 }}>{when}</p>
      )}
    </div>
  )
}

function LifeStories({ life }) {
  if (!life) {
    return (
      <div className="flex items-center justify-center h-24">
        <span style={{ color: kawaii.inkSoft, fontFamily: ui.font, fontSize: 12.5, fontWeight: 650 }}>
          reading what is still moving...
        </span>
      </div>
    )
  }

  const now = life.current?.now || null
  const change = life.current?.latestChange || null
  const want = life.commitments?.[0] || null
  const question = life.questions?.[0] || null
  const stories = life.stories || []
  // Consequences first: a note he acted on outranks a note he has only
  // answered, however recent the answer.
  const influences = [...(life.influences || [])]
    .sort((a, b) => (INFLUENCE_RANK[b.stage] || 0) - (INFLUENCE_RANK[a.stage] || 0) || (b.updatedAt || 0) - (a.updatedAt || 0))
    .slice(0, 3)
  const beliefs = (life.beliefs || []).slice(0, 3)

  const present = now?.line || want || question || change
  if (!present && !stories.length && !influences.length && !beliefs.length) {
    return (
      <div style={{ color: kawaii.inkSoft, fontFamily: ui.font, fontSize: 13, fontWeight: 650, lineHeight: 1.55, padding: '10px 2px' }}>
        Nothing is pulling hard enough to call a story right now. His diary still has the day as it happened.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {present && (
        <section className="flex flex-col gap-3" style={{ background: 'rgba(124,92,255,0.08)', border: '1px solid rgba(124,92,255,0.3)', borderRadius: 12, padding: '11px 12px' }}>
          {now?.line && (
            <Line eyebrow={now.person ? `right now, because of ${now.person}` : 'right now'}>
              {BIRD_NAME} is {now.line}.
            </Line>
          )}
          {want && <Line eyebrow="what he means to do">{wantSentence(want)}</Line>}
          {question && <Line eyebrow="a question he is carrying">{questionSentence(question)}</Line>}
          {change?.text && (
            <Line eyebrow="what changed lately" when={`${relativeTime(change.at)}${change.person ? `, after ${change.person}` : ''}`}>
              {change.text}
            </Line>
          )}
        </section>
      )}

      {stories.map((story) => (
        <section key={story.id} style={{ borderTop: kawaii.stitch, paddingTop: 13 }}>
          <Eyebrow>{storyEyebrow(story)}</Eyebrow>
          <p style={{ color: kawaii.ink, fontFamily: ui.font, fontSize: 14.5, fontWeight: 850, lineHeight: 1.3, marginTop: 2 }}>{story.title}</p>
          {story.question && (
            <p style={{ color: kawaii.inkSoft, fontFamily: ui.font, fontSize: 13, fontWeight: 650, lineHeight: 1.4, marginTop: 3 }}>{story.question}</p>
          )}
          {story.stakes && (
            <p style={{ color: kawaii.inkSoft, fontFamily: ui.font, fontSize: 12.5, fontWeight: 600, lineHeight: 1.45, marginTop: 5 }}>{story.stakes}</p>
          )}
          {/* How it began, said once, so the steps under it read as one
              story from its start (Sam, 27 Oct: "threads that go on for a
              long time"). */}
          {story.started?.how && (
            <p style={{ color: kawaii.inkSoft, fontFamily: ui.font, fontSize: 12.5, fontWeight: 650, lineHeight: 1.45, marginTop: 4 }}>
              It started {relativeTime(story.started.at)}: {story.started.how}.
            </p>
          )}

          {!!story.events?.length && (
            <ol className="flex flex-col gap-2" style={{ marginTop: 10, listStyle: 'none', padding: 0 }}>
              {story.events.map((event, i) => (
                <li key={event.id}>
                  <p style={{ color: kawaii.ink, fontFamily: ui.font, fontSize: 13, fontWeight: 620, lineHeight: 1.45 }}>{event.text}</p>
                  {event.because && (
                    <p style={{ color: '#5f42ab', fontFamily: ui.font, fontSize: 12, fontWeight: 620, lineHeight: 1.4, marginTop: 1 }}>his reason: {event.because}</p>
                  )}
                  <p style={{ color: kawaii.inkSoft, fontFamily: ui.font, fontSize: 12, fontWeight: 700, marginTop: 2 }}>
                    {relativeTime(event.ts)}{event.person ? `, after ${event.person}` : ''}
                  </p>
                  {i === 0 && story.count > story.events.length && (
                    <p style={{ color: kawaii.inkSoft, fontFamily: ui.font, fontSize: 12, fontWeight: 760, marginTop: 6 }}>
                      {story.count - story.events.length} more along the way
                    </p>
                  )}
                </li>
              ))}
            </ol>
          )}

          <p style={{ color: '#5f42ab', fontFamily: ui.font, fontSize: 12, fontWeight: 760, lineHeight: 1.4, marginTop: 8 }}>
            {STAGE_WORDS[story.stage] || story.stage}{story.published ? ', and it is up on his own site' : ''}
          </p>
        </section>
      ))}

      {!!influences.length && (
        <section style={{ borderTop: kawaii.stitch, paddingTop: 13 }}>
          <Eyebrow>what people set off</Eyebrow>
          <div className="flex flex-col gap-2" style={{ marginTop: 6 }}>
            {influences.map((influence) => (
              <p key={influence.id} style={{ color: kawaii.ink, fontFamily: ui.font, fontSize: 13, fontWeight: 620, lineHeight: 1.45 }}>
                {influenceSentence(influence)}
              </p>
            ))}
          </div>
        </section>
      )}

      {!!beliefs.length && (
        <section style={{ borderTop: kawaii.stitch, paddingTop: 13 }}>
          <Eyebrow>what he thinks he knows</Eyebrow>
          <div className="flex flex-col gap-2" style={{ marginTop: 6 }}>
            {beliefs.map((belief) => (
              <div key={belief.id}>
                <p style={{ color: kawaii.ink, fontFamily: ui.font, fontSize: 13, fontWeight: 620, lineHeight: 1.45 }}>{belief.proposition}</p>
                {belief.status === 'contested' && (
                  <p style={{ color: kawaii.inkSoft, fontFamily: ui.font, fontSize: 12, fontWeight: 700, marginTop: 2 }}>he is not so sure about this any more</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function JournalHUD({ npc = 'victor' }) {
  const [entries, setEntries] = useState([])
  // The fetch already asks for these types; filtered here too because the
  // viewer and the sim deploy separately, so there is a window where this
  // build is talking to a sim that returns everything. Folded into
  // stories: the walk pulls an entry's ancestors up underneath it, so a
  // consequence and its causes read as one thing.
  const stories = useMemo(
    () => buildStories(entries
      .filter((entry) => RENDERED_TYPES.has(entry.type))
      // the town's narrator wrote these in his name until the mind took his voice back. said again in his voice
      // they stayed a list of rain and knobs, so they are not shown: the diary is his words. a verdict at the shrine
      // carries his answer and stays. all of it is still in journal.jsonl and on /admin
      .filter((entry) => !FAILURE_OUTCOMES.has(entry.outcome))
      .filter((entry) => !INTERNAL_OUTCOMES.has(entry.outcome)))
      // A held entry only exists on the page because something names it as
      // a cause, so it renders inside that something's story or not at
      // all: if its claimant got filtered on this side, it has no earned
      // place and standing alone would undo the gate.
      .filter((row) => !row.entry.held),
    [entries],
  )
  const visitorStories = useMemo(() => stories.filter(({ entry }) => (
    entry.type === 'wish'
      || !!entry.handle
      || !!entry.from
      || entry.outcome === 'answered_talk'
      || entry.outcome === 'told_them'
      || /\b(note|message|picture|letter)\b/i.test(entry.cause?.label || '')
      // what he made of something a visitor left in the town (a coin on
      // the payphone, a penny by the casino door): theirs as much as his,
      // and 68 of 400 entries on his day (Sam, 27 Oct: "yes do the quieter
      // day tab next")
      || entry.traceRef != null
  )), [stories])
  const dayStories = useMemo(() => stories.filter((story) => !visitorStories.includes(story)), [stories, visitorStories])
  // Keys by timestamp, not by position. Keyed on the index, one new line
  // at the top shifted every key below it and React tore down and
  // rebuilt all sixty cards on each poll, each heart re-reading
  // localStorage on the way back in. Two entries sharing a millisecond
  // (it happens, a verdict and its moment) get a suffix rather than a
  // collision.
  const keys = useMemo(() => {
    const out = new Map()
    const used = new Map()
    for (const { entry } of stories) {
      const n = used.get(entry.ts) || 0
      used.set(entry.ts, n + 1)
      out.set(entry, n ? `${entry.ts}-${n}` : String(entry.ts))
    }
    return out
  }, [stories])
  const [mind, setMind] = useState(null)
  // Opened from the corner menu; the panel still owns closing itself.
  const open = useGameStore((st) => st.journalOpen)
  const setOpen = useGameStore((st) => st.setJournalOpen)
  const journalFocus = useGameStore((st) => st.journalFocus)
  const setJournalFocus = useGameStore((st) => st.setJournalFocus)
  // The about tab is every line that names you, read
  // from the whole of his journal rather than the rolling window, so a
  // mention from a fortnight ago is still there to be found.
  const tab = useGameStore((st) => st.journalTab)
  const setTab = useGameStore((st) => st.setJournalTab)
  const life = useGameStore((st) => st.lifeState)
  const setLife = useGameStore((st) => st.setLifeState)
  // which entries are steps of a longer story, by their time (the stories tab's strands)
  const strandByTs = useMemo(() => {
    const out = new Map()
    for (const story of life?.stories || []) {
      if (!['asked', 'looking', 'making'].includes(story.kind)) continue
      for (const ts of story.stepTs || []) if (!out.has(ts)) out.set(ts, { id: story.id, title: story.title })
    }
    return out
  }, [life])
  // His day, quieter (Sam, 27 Oct: "yes do the quieter day tab next"). A
  // page he read goes on it when it is part of something he keeps looking
  // into (a strand), when something sent him to it, or when something later
  // came of it (an entry names it as its cause). A look that went nowhere is
  // still in his journal and on admin: 15 of the 49 reads on the day tab in
  // the 38 hours to 27 Oct. Until his life has loaded nothing is held back,
  // so the strands are known before a read is left out.
  const shownDay = useMemo(() => {
    if (!life) return dayStories
    const claimed = new Set(entries.map((e) => e.cause?.ts).filter(Boolean))
    return dayStories.filter(({ entry }) => !(entry.kind === 'internet' && entry.outcome === 'read'
      && !entry.cause && !strandByTs.has(entry.ts) && !claimed.has(entry.ts)))
  }, [life, dayStories, strandByTs, entries])
  const [mentions, setMentions] = useState(null)
  const [personal, setPersonal] = useState(null)
  const { signedIn } = useAccount()
  const [total, setTotal] = useState(0)
  const seenTotalRef = useRef(null)
  const sigRef = useRef('')
  const scrollRef = useRef(null)
  // what is held now, for the poll to merge into without waiting on a render, and whether it is his whole day
  const entriesRef = useRef([])
  const dayRef = useRef(false)

  const fetchJournal = useCallback(async () => {
    try {
      // Ask the server for the types we actually draw, rather than asking
      // for everything and throwing most of it away here.
      //
      // Over-fetching was the old workaround: the window came back mostly
      // decisions and completions, so 120 left the panel thin and it went
      // to 240 to put the card count back. In prod that was 400 lines in
      // to 40 cards out. Filtering upstream means a smaller request returns
      // more diary, and the header count and corner badge start counting
      // entries that exist rather than rows nobody sees.
      //
      // Derived from RENDERED_TYPES so this can never drift from what
      // JournalEntry knows how to render.
      const want = [...RENDERED_TYPES].join(',')
      // His whole day, then what is new. Every poll asked for the newest 60,
      // and by 30 Oct sixty was forty minutes of him (26 answers at the
      // shrine and 20 things noticed, in one stretch), so his day on the
      // diary was the last forty minutes of it (Sam, 30 Oct: "why is his day
      // in his diary only showing the last 38 minutes of activity?"). Opened,
      // the panel reads DAY_LINES once, about four and a half hours at that
      // pace and longer than one of his days, and so does a jump to an entry
      // further back. Each poll after asks for POLL_LINES and lays them over
      // what is held, newest first. A poll whose every line is newer than
      // anything held has a gap behind it (a tab left asleep), and reads the
      // day again. Shut, the poll only feeds the unread dot and keeps the
      // newest page as it always did: the day is 147 KB against 26, for
      // every visitor whether or not they open the diary.
      const isOpen = useGameStore.getState().journalOpen
      const held = entriesRef.current
      const read = async (limit) => {
        const res = await fetch(`/api/journal?npc=${npc}&limit=${limit}&types=${want}`)
        return res.ok ? res.json() : null
      }
      let whole = !!useGameStore.getState().journalFocus || (isOpen && !dayRef.current)
      let data = await read(whole ? DAY_LINES : POLL_LINES)
      if (!data) return
      const behind = data.entries || []
      if (!whole && isOpen && behind.length >= POLL_LINES && held.length && behind[behind.length - 1].ts > held[0].ts) {
        whole = true
        data = await read(DAY_LINES)
        if (!data) return
      }
      const fresh = data.entries || []
      dayRef.current = whole || (isOpen && dayRef.current)
      let list = fresh
      if (!whole && isOpen) {
        // a line read again replaces the one held, so its hearts are the
        // ones counted now
        const keyOf = (e) => `${e.ts}|${e.type}|${e.kind || ''}|${e.outcome || ''}`
        const seenKeys = new Set(fresh.map(keyOf))
        list = [...fresh, ...held.filter((e) => !seenKeys.has(keyOf(e)))]
          .sort((a, b) => b.ts - a.ts)
          .slice(0, DAY_LINES)
      }
      // Same page, same state: most polls return exactly what the last one
      // did, and setting it anyway re-ran the story walk and re-rendered
      // every card for nothing. Hearts ride the signature because a count
      // moving is the one thing that changes on an old entry.
      const sig = list.map((e) => `${e.ts}:${e.likes || 0}`).join(',') + `|${data.mind?.text || ''}|${data.mind?.chased || 0}`
      if (sig !== sigRef.current) {
        sigRef.current = sig
        entriesRef.current = list
        setEntries(list)
        setMind(data.mind || null)
      }
      const nextTotal = data.total || 0
      // Badge the menu when new entries land while the panel is shut. The
      // first poll just establishes a baseline, otherwise every page load
      // would announce his entire back catalogue as unread.
      const seen = seenTotalRef.current
      if (seen !== null && nextTotal > seen && !useGameStore.getState().journalOpen) {
        useGameStore.getState().setJournalUnseen(true)
      }
      seenTotalRef.current = nextTotal
      setTotal(nextTotal)
    } catch {
      // Server likely not running
    }
  }, [npc])

  // The jump: land on the right tab, refetch wide, scroll to the row and
  // light it. The focus clears itself so the tint fades and a later open
  // starts plain. A jump that names the about tab goes there because the
  // diary window may have rolled past the entry; the about tab never has.
  useEffect(() => {
    if (!open || !journalFocus) return
    if (journalFocus.tab === 'about') setTab('about')
    else fetchJournal()
    const t = setTimeout(() => setJournalFocus(null), 6000)
    return () => clearTimeout(t)
  }, [open, journalFocus, fetchJournal, setJournalFocus, setTab])

  // The about tab reads on entry, not on a poll: mentions move at the
  // pace of his verdicts and finds, and the tab being open is the ask.
  useEffect(() => {
    if (!open || tab !== 'about' || !signedIn) return
    let dead = false
    authFetch('/api/mentions', { cache: 'no-store' })
      .then((r) => r.json())
      .then((d) => {
        if (dead || !Array.isArray(d.mentions)) return
        setMentions(d.mentions)
        setPersonal(d.personal || null)
      })
      .catch(() => { /* keep stale */ })
    // Opening the tab is looking: the dot goes out.
    authFetch('/api/mentions', { method: 'POST' }).catch(() => {})
    return () => { dead = true }
  }, [open, tab, signedIn])

  // LifeNow normally keeps this warm. Read again on open so the deeper
  // story view never waits for the background polling interval.
  useEffect(() => {
    if (!open) return
    let dead = false
    fetch(`/api/life?npc=${npc}`, { cache: 'no-store' })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => { if (!dead && data) setLife(data) })
      .catch(() => {})
    return () => { dead = true }
  }, [open, npc, setLife])

  useEffect(() => {
    fetchJournal()
    const id = setInterval(fetchJournal, open ? POLL_OPEN_MS : POLL_CLOSED_MS)
    return () => clearInterval(id)
  }, [fetchJournal, open])

  // Display name, never the id: the id stays `victor` everywhere internal.
  const npcLabel = BIRD_NAME
  return (
    <>
      {/* ── Panel ── */}
      {open && (
        <>
          {/* Mobile backdrop */}
          <div
            className="md:hidden fixed inset-0 z-40 bg-black/60"
            onClick={() => setOpen(false)}
          />

          <div
            className={[
              // 55: above the HUD chrome (clock and menu sit at z-50). At
              // equal z the clock floated over the diary header on phones.
              'fixed z-[55] pointer-events-auto',
              // Mobile: full-screen overlay
              'top-0 left-0 right-0 bottom-0',
              // Desktop: constrained left panel — top/bottom both pinned so the
              // inner h-full flex column gets a real height, not content-sized
              // (which was making it grow with every new journal entry).
              // Top edge lines up with the needs card (top-4); the bottom
              // stops short of the minimap disc in the corner (132px + its
              // own 24px inset), so the two never overlap.
              'md:top-4 md:left-6 md:bottom-[10.5rem] md:right-auto md:w-[370px]',
            ].join(' ')}
          >
            <div
              className="relative h-full flex flex-col overflow-hidden"
              style={{ ...kawaiiPanel(), borderRadius: 22 }}
            >
              <div style={{ position: 'absolute', inset: 4, border: kawaii.stitch, borderRadius: 18, pointerEvents: 'none', zIndex: 2 }} />
              {/* Header */}
              <div
                className="flex items-center justify-between px-4 py-3"
                style={{ borderBottom: kawaii.stitch }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <BookIcon size={17} />
                  <span style={{ color: kawaii.ink, fontFamily: ui.font, fontSize: 14, fontWeight: 800 }}>
                    {npcLabel}'s diary
                  </span>
                  {total > 0 && (
                    <span style={{ color: kawaii.inkSoft, fontFamily: ui.font, fontSize: 11 }}>{total}</span>
                  )}
                </div>
                <CloseButton onClick={() => setOpen(false)} />
              </div>

              {/* Five reads of the same life: what is unfolding, his own
                  day, what he has asked the world for, visitor contact,
                  and the lines that name you. */}
              <div className="flex items-center gap-2 overflow-x-auto px-4 pt-2.5 pb-2" style={{ borderBottom: kawaii.stitch }}>
                {[['stories', 'stories'], ['diary', 'his day'], ['wants', 'he wants'], ['visitors', 'visitors'], ['about', 'about you']].map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTab(key)}
                    style={{
                      background: tab === key ? kawaii.ink : 'transparent',
                      color: tab === key ? kawaii.cream : kawaii.inkSoft,
                      border: tab === key ? kawaii.outlineThin : '2px solid transparent',
                      borderRadius: 999,
                      padding: '4px 10px',
                      fontFamily: ui.font,
                      fontSize: 12,
                      fontWeight: 800,
                      cursor: 'pointer',
                      flex: '0 0 auto',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {tab === 'stories' && (
                <div className="kawaii-scroll flex-1 overflow-y-auto px-4 py-3">
                  <LifeStories life={life} />
                </div>
              )}

              {tab === 'wants' && (
                <div className="kawaii-scroll flex-1 overflow-y-auto px-4 py-3">
                  <Wants />
                </div>
              )}

              {tab === 'about' && (
                <div className="kawaii-scroll flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3">
                  {signedIn && personal?.relationships?.[0] && (
                    <div style={{ background: 'rgba(124,92,255,0.08)', border: '1px solid rgba(124,92,255,0.3)', borderRadius: 11, padding: '10px 11px' }}>
                      <p style={{ color: '#5f42ab', fontFamily: ui.font, fontSize: 12, fontWeight: 850 }}>
                        {personal.relationships[0].state} to him
                      </p>
                      <p style={{ color: kawaii.inkSoft, fontFamily: ui.font, fontSize: 12.5, fontWeight: 620, lineHeight: 1.45, marginTop: 2 }}>
                        {personal.relationships[0].touches} contact{personal.relationships[0].touches === 1 ? '' : 's'}, {personal.relationships[0].consequences} later consequence{personal.relationships[0].consequences === 1 ? '' : 's'}
                      </p>
                    </div>
                  )}
                  {signedIn && !!personal?.influences?.length && (
                    <section>
                      <p style={{ color: kawaii.ink, fontFamily: ui.font, fontSize: 13, fontWeight: 850 }}>what changed after you visited</p>
                      <div className="flex flex-col gap-2" style={{ marginTop: 7 }}>
                        {personal.influences.map((influence) => (
                          <div key={influence.id} style={{ borderLeft: '2px solid rgba(95,66,171,0.3)', paddingLeft: 9 }}>
                            <p style={{ color: '#5f42ab', fontFamily: ui.font, fontSize: 12, fontWeight: 800 }}>{influence.stage.replaceAll('_', ' ')}</p>
                            <p style={{ color: kawaii.inkSoft, fontFamily: ui.font, fontSize: 12.5, fontWeight: 620, lineHeight: 1.45, marginTop: 1 }}>
                              {influence.consequence || influence.latest || influence.source}
                            </p>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}
                  {!signedIn ? (
                    <p style={{ color: kawaii.inkSoft, fontFamily: ui.font, fontSize: 13, fontWeight: 600, lineHeight: 1.5, padding: '8px 2px' }}>
                      connect first, and this page becomes every line of his
                      diary that names you.
                    </p>
                  ) : mentions === null ? (
                    <p style={{ color: kawaii.inkSoft, fontFamily: ui.font, fontSize: 12.5, fontWeight: 600, padding: '8px 2px' }}>reading back through his days...</p>
                  ) : mentions.length === 0 && !personal?.influences?.length ? (
                    <p style={{ color: kawaii.inkSoft, fontFamily: ui.font, fontSize: 13, fontWeight: 600, lineHeight: 1.5, padding: '8px 2px' }}>
                      nothing yet. leave him something, or do something in the
                      town, and the first line about you lands here.
                    </p>
                  ) : (
                    mentions.map((m, i) => {
                      const focused = journalFocus && m.ref != null && Number(m.ref) === Number(journalFocus.ref)
                      return (
                        <div
                          key={`${m.ts}-${i}`}
                          ref={focused ? (node) => { if (node) node.scrollIntoView({ block: 'center', behavior: 'smooth' }) } : undefined}
                          className="border-t border-[#2f2650]/25 pt-3 first:border-0 first:pt-0"
                          style={focused ? { background: '#e9defc', borderRadius: 10, padding: '10px 8px', transition: 'background 1.5s ease' } : undefined}
                        >
                          <div className="flex items-baseline gap-2 mb-1">
                            <span style={{ color: kawaii.inkSoft, fontFamily: ui.font, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.04em' }}>
                              {new Date(m.ts).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <p style={{ color: kawaii.ink, fontFamily: ui.font, fontSize: 13.5, fontWeight: 600, lineHeight: 1.55 }}>
                            {m.line}
                          </p>
                        </div>
                      )
                    })
                  )}
                </div>
              )}

              {tab === 'visitors' && (
                <div className="kawaii-scroll flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-4">
                  {!visitorStories.length ? (
                    <p style={{ color: kawaii.inkSoft, fontFamily: ui.font, fontSize: 13, fontWeight: 600, lineHeight: 1.5, padding: '8px 2px' }}>
                      Nobody has reached into his day yet.
                    </p>
                  ) : visitorStories.map(({ entry, ancestors }) => (
                    <div key={keys.get(entry)} className="border-t border-[#2f2650]/25 pt-3 first:border-0 first:pt-0">
                      {ancestors ? (
                        <StoryGroup ancestors={ancestors} head={entry}>
                          <JournalEntry entry={entry} />
                        </StoryGroup>
                      ) : <JournalEntry entry={entry} />}
                    </div>
                  ))}
                </div>
              )}

              {/* Entries */}
              {tab === 'diary' && (
              <div ref={scrollRef} className="kawaii-scroll flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-4">
                {!shownDay.length ? (
                  <div className="flex items-center justify-center h-20">
                    <span className="text-[10px] text-[#2f2650]/60 uppercase tracking-widest">
                      No entries yet
                    </span>
                  </div>
                ) : (
                  <>
                    {/* The arc, said outright. The entries below imply it,
                        but a visitor should not have to infer that the last
                        four are one story. When he is carrying something,
                        the journal names it before anything else. */}
                    {mind && (
                      <div className="mb-3 px-2.5 py-2 rounded-md" style={{ background: 'rgba(124,92,255,0.08)', border: '1px solid rgba(124,92,255,0.35)' }}>
                        <span className="text-[11px] tracking-[0.02em] font-bold" style={{ color: '#5f42ab' }}>on his mind</span>
                        <p className="text-[13px] leading-snug" style={{ color: kawaii.ink }}>
                          {mind.text}
                          <span className="text-[11px]" style={{ color: 'rgba(47,38,80,0.55)' }}> · since {mind.ago}{mind.chased > 1 ? ` · gone back to it ${mind.chased} times` : ''}</span>
                        </p>
                      </div>
                    )}
                    <MoodArc entries={entries} />
                    {shownDay.map(({ entry, ancestors }) => {
                        const focused = journalFocus && entry.traceRef != null && Number(entry.traceRef) === Number(journalFocus.ref)
                        return (
                          <div
                            key={keys.get(entry)}
                            ref={focused ? (node) => { if (node) node.scrollIntoView({ block: 'center', behavior: 'smooth' }) } : undefined}
                            className="border-t border-[#2f2650]/25 pt-3 first:border-0 first:pt-0"
                            style={focused ? { background: '#e9defc', borderRadius: 10, padding: '10px 8px', transition: 'background 1.5s ease' } : undefined}
                          >
                            {ancestors ? (
                              <StoryGroup ancestors={ancestors} head={entry}>
                                <JournalEntry entry={entry} strand={strandByTs.get(entry.ts)} onStrand={() => setTab('stories')} />
                              </StoryGroup>
                            ) : (
                              <JournalEntry entry={entry} strand={strandByTs.get(entry.ts)} onStrand={() => setTab('stories')} />
                            )}
                          </div>
                        )
                      })}
                  </>
                )}
              </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default JournalHUD
